import { callApi, authFetch } from "../../api/api";

const BASE = "/api/performance-tracker";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

function newId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function unwrap(res) {
  return res?.data?.data ?? null;
}

function mutationHeaders(idempotencyKey) {
  return {
    "Idempotency-Key": idempotencyKey || newId(),
    "X-Correlation-Id": newId(),
  };
}

export async function getCurrentSession(instance, accounts) {
  const res = await callApi(instance, accounts, "GET", `${BASE}/current`, null, {
    headers: { "X-Correlation-Id": newId() },
  });
  return unwrap(res);
}

export async function startSession(instance, accounts, { idempotencyKey } = {}) {
  const res = await callApi(instance, accounts, "POST", `${BASE}/start`, {}, {
    headers: mutationHeaders(idempotencyKey),
  });
  return unwrap(res);
}

export async function pauseSession(instance, accounts, { sessionId, comment, idempotencyKey }) {
  const res = await callApi(
    instance,
    accounts,
    "POST",
    `${BASE}/pause`,
    { sessionId, comment },
    { headers: mutationHeaders(idempotencyKey) },
  );
  return unwrap(res);
}

export async function resumeSession(instance, accounts, { sessionId, resumeType, idempotencyKey }) {
  const res = await callApi(
    instance,
    accounts,
    "POST",
    `${BASE}/resume`,
    { sessionId, resumeType },
    { headers: mutationHeaders(idempotencyKey) },
  );
  return unwrap(res);
}

export async function stopSession(instance, accounts, { sessionId, comment, reason, idempotencyKey }) {
  const res = await callApi(
    instance,
    accounts,
    "POST",
    `${BASE}/stop`,
    { sessionId, comment, reason },
    { headers: mutationHeaders(idempotencyKey) },
  );
  return unwrap(res);
}

export async function stopSessionOnLogout(instance, accounts, { sessionId }) {
  const url = `${String(API_BASE_URL).replace(/\/$/, "")}${BASE}/stop`;
  const headers = {
    "Content-Type": "application/json",
    ...mutationHeaders(),
  };
  const body = JSON.stringify({
    sessionId,
    reason: "LOGOUT",
    comment: "LOGOUT",
  });

  try {
    await authFetch(instance, accounts, url, {
      method: "POST",
      keepalive: true,
      headers,
      body,
    });
  } catch {
    /* best-effort; logout continues */
  }
}

export async function pingPresence(instance, accounts) {
  const res = await callApi(instance, accounts, "POST", `${BASE}/presence/ping`, {}, {
    headers: { "X-Correlation-Id": newId() },
  });
  return unwrap(res);
}

export async function clearPresenceOnLogout(instance, accounts) {
  const url = `${String(API_BASE_URL).replace(/\/$/, "")}${BASE}/presence`;
  try {
    await authFetch(instance, accounts, url, {
      method: "DELETE",
      keepalive: true,
      headers: { "X-Correlation-Id": newId() },
    });
  } catch {
    /* best-effort; logout continues */
  }
}

export async function recordActivity(
  instance,
  accounts,
  { sessionId, signal, clientTimestamp, idempotencyKey } = {},
) {
  const res = await callApi(
    instance,
    accounts,
    "POST",
    `${BASE}/activity`,
    { sessionId, signal, clientTimestamp },
    { headers: mutationHeaders(idempotencyKey) },
  );
  return unwrap(res);
}

export function createTrackerRequestId() {
  return newId();
}

export async function getActivity(instance, accounts, { from, to, userId } = {}) {
  const params = { from, to };
  if (userId) params.userId = userId;
  const res = await callApi(instance, accounts, "GET", `${BASE}/activity`, null, {
    params,
    headers: { "X-Correlation-Id": newId() },
  });
  return unwrap(res);
}

export async function listVisibleUsers(instance, accounts, { q } = {}) {
  const res = await callApi(instance, accounts, "GET", `${BASE}/users`, null, {
    params: q ? { q } : {},
    headers: { "X-Correlation-Id": newId() },
  });
  return unwrap(res);
}

export async function getSessionEvents(instance, accounts, sessionId) {
  const res = await callApi(
    instance,
    accounts,
    "GET",
    `${BASE}/sessions/${sessionId}/events`,
    null,
    { headers: { "X-Correlation-Id": newId() } },
  );
  return unwrap(res);
}

export async function replayPendingItem(instance, accounts, item) {
  if (!item?.kind) return null;
  if (item.kind === "activity") {
    return recordActivity(instance, accounts, {
      sessionId: item.sessionId,
      signal: item.signal,
      clientTimestamp: item.clientTimestamp,
      idempotencyKey: item.idempotencyKey,
    });
  }
  if (item.kind === "start") {
    return startSession(instance, accounts, { idempotencyKey: item.idempotencyKey });
  }
  if (item.kind === "pause") {
    return pauseSession(instance, accounts, {
      sessionId: item.sessionId,
      comment: item.comment,
      idempotencyKey: item.idempotencyKey,
    });
  }
  if (item.kind === "resume") {
    return resumeSession(instance, accounts, {
      sessionId: item.sessionId,
      resumeType: item.resumeType || "MANUAL",
      idempotencyKey: item.idempotencyKey,
    });
  }
  if (item.kind === "stop") {
    return stopSession(instance, accounts, {
      sessionId: item.sessionId,
      comment: item.comment,
      reason: item.reason,
      idempotencyKey: item.idempotencyKey,
    });
  }
  return null;
}
